import { SectionCard } from "@/components/common/SectionCard";
import { SectionTabSlider } from "@/components/proposal-formats/SectionTabSlider";
import { ServiceCollapsibleSection } from "./ServiceCollapsibleSection";
import { ghostBtnClass, inputClass, primaryBtnClass, techLabel } from "./serviceShared";
import type { useServiceDefinitionsPanel } from "./useServiceDefinitionsPanel";
import type { PageSizeOption } from "./servicePanelUtils";

const RESOURCES_TABS = [
  { id: "rigs" as const, label: "Rigs & equipment" },
  { id: "techs" as const, label: "Technicians" },
];

const PAGE_SIZE_OPTIONS: PageSizeOption[] = [5, 10, 20];

export function ServiceDefinitionResourcesSection(props: {
  m: ReturnType<typeof useServiceDefinitionsPanel>;
  title?: string;
  description?: string;
}) {
  const { m, title, description } = props;
  const r = m.resources;
  const isGeneral = m.scope === "general";
  const scopeLabel = isGeneral ? "General" : (m.activeOffering?.label ?? "Service");
  const cardTitle = title ?? `Resources — ${scopeLabel}`;
  const cardDescription =
    description ??
    (isGeneral
      ? "Org-wide rigs and technicians. Click a row to edit; click again to close (save first if you changed anything)."
      : "Resources linked to this service. New entries here are auto-associated.");

  return (
    <SectionCard title={cardTitle}>
      <p className="mb-3 text-sm text-gray-600 dark:text-zinc-400">{cardDescription}</p>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <SectionTabSlider
          ariaLabel="Resources type"
          options={RESOURCES_TABS}
          value={m.resourcesTab}
          onChange={m.setResourcesTab}
        />
        <PageSizePicker pageSize={r.pageSize} onChange={r.setPageSize} />
      </div>

      {m.resourcesTab === "rigs" ? <RigsBlock m={m} /> : <TechsBlock m={m} />}
    </SectionCard>
  );
}

function PageSizePicker(props: { pageSize: PageSizeOption; onChange: (n: PageSizeOption) => void }) {
  return (
    <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-zinc-400">
      Per page
      <select
        className={inputClass}
        value={props.pageSize}
        onChange={(e) => props.onChange(Number(e.target.value) as PageSizeOption)}
      >
        {PAGE_SIZE_OPTIONS.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    </label>
  );
}

function ResourceNoteFields(props: {
  noteDraft: { body: string; expires_on: string };
  setNoteDraft: React.Dispatch<React.SetStateAction<{ body: string; expires_on: string }>>;
  onDismiss?: () => void;
  dismissBusy?: boolean;
}) {
  const { noteDraft, setNoteDraft, onDismiss, dismissBusy } = props;
  const hasNote = Boolean(noteDraft.body.trim() || noteDraft.expires_on.trim());
  return (
    <div className="mt-3">
      <ServiceCollapsibleSection
        title="Team note / alert"
        subtitle={hasNote ? "Draft in progress" : "Optional team-visible note or alert"}
        defaultOpen={false}
        headerRight={
          onDismiss ? (
            <button
              type="button"
              className={`${ghostBtnClass} text-xs`}
              disabled={dismissBusy}
              onClick={onDismiss}
            >
              Dismiss alert
            </button>
          ) : undefined
        }
      >
        <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-3 dark:border-amber-900 dark:bg-amber-950/25">
          <textarea
            className={`${inputClass} min-h-[60px] text-sm`}
            placeholder="e.g. John is out until 6/30 — rig axle at Jimmy's Auto until 6/30"
            value={noteDraft.body}
            onChange={(e) => setNoteDraft((d) => ({ ...d, body: e.target.value }))}
          />
          <label className="mt-2 block text-xs text-gray-600 dark:text-zinc-400">
            Expires on (optional — auto-archives after this date)
            <input
              type="date"
              className={`${inputClass} mt-1`}
              value={noteDraft.expires_on}
              onChange={(e) => setNoteDraft((d) => ({ ...d, expires_on: e.target.value }))}
            />
          </label>
        </div>
      </ServiceCollapsibleSection>
    </div>
  );
}

function PaginationBar(props: {
  page: number;
  pages: number;
  total: number;
  onPage: (p: number) => void;
}) {
  if (props.total <= 0) return null;
  return (
    <div className="mt-3 flex items-center justify-between text-xs text-gray-600 dark:text-zinc-400">
      <span>
        {props.total} total · page {props.page} of {props.pages}
      </span>
      <div className="flex gap-1">
        <button
          type="button"
          className={ghostBtnClass}
          disabled={props.page <= 1}
          onClick={() => props.onPage(props.page - 1)}
        >
          Prev
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          disabled={props.page >= props.pages}
          onClick={() => props.onPage(props.page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function RigsBlock(props: { m: ReturnType<typeof useServiceDefinitionsPanel> }) {
  const { m } = props;
  const r = m.resources;
  const editing = r.selectedRigId !== null;
  const activeNoteId = editing
    ? r.notesForResource("rig", r.selectedRigId!)[0]?.id
    : undefined;

  return (
    <>
      <div className="mb-4 grid gap-2 sm:grid-cols-3">
        <input
          className={inputClass}
          placeholder="Designation"
          value={r.rigDraft.designation}
          onChange={(e) => r.setRigDraft((d) => ({ ...d, designation: e.target.value }))}
        />
        <input
          className={inputClass}
          placeholder="Kind (e.g. repair_truck)"
          value={r.rigDraft.rig_kind}
          onChange={(e) => r.setRigDraft((d) => ({ ...d, rig_kind: e.target.value }))}
        />
        <select
          className={inputClass}
          value={r.rigDraft.standard_tech_id}
          onChange={(e) => r.setRigDraft((d) => ({ ...d, standard_tech_id: e.target.value }))}
        >
          <option value="">Standard tech (optional)</option>
          {m.allTechs.map((t) => (
            <option key={t.id} value={t.id}>
              {t.display_name}
            </option>
          ))}
        </select>
      </div>

      <ResourceNoteFields
        noteDraft={r.noteDraft}
        setNoteDraft={r.setNoteDraft}
        onDismiss={
          activeNoteId
            ? () => void r.dismissNote(activeNoteId)
            : undefined
        }
        dismissBusy={r.actionBusy}
      />

      <button
        type="button"
        className={`${primaryBtnClass} mt-3 mb-4`}
        disabled={r.actionBusy}
        onClick={() => void r.saveRig()}
      >
        {r.actionBusy ? "Saving…" : r.rigDraft.id ? "Save rig" : "Add rig"}
      </button>

      {r.scopedRigs.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">No rigs in this scope yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
          {r.pagedRigs.map((rig) => {
            const selected = r.selectedRigId === rig.id;
            const alert = r.notesForResource("rig", rig.id)[0];
            return (
              <li key={rig.id}>
                <button
                  type="button"
                  className={
                    "flex w-full flex-wrap items-center justify-between gap-2 py-2 text-left rounded-lg px-1 -mx-1 " +
                    (selected
                      ? "bg-indigo-50 ring-1 ring-indigo-200 dark:bg-indigo-950/40 dark:ring-indigo-800"
                      : "hover:bg-gray-50 dark:hover:bg-zinc-900/50")
                  }
                  onClick={() => r.selectRig(rig)}
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                      {alert ? <span className="mr-1 text-amber-600">!</span> : null}
                      {rig.designation}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-zinc-400">
                      {rig.rig_kind} · {techLabel(m.allTechs, rig.standard_tech_id)}
                      {alert ? ` · ${alert.body.slice(0, 48)}${alert.body.length > 48 ? "…" : ""}` : ""}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <PaginationBar page={r.rigPage} pages={r.rigPages} total={r.scopedRigs.length} onPage={r.setRigPage} />
    </>
  );
}

function TechsBlock(props: { m: ReturnType<typeof useServiceDefinitionsPanel> }) {
  const { m } = props;
  const r = m.resources;
  const editing = r.selectedTechId !== null;
  const activeNoteId = editing
    ? r.notesForResource("tech", r.selectedTechId!)[0]?.id
    : undefined;

  return (
    <>
      <div className="mb-4 grid gap-2 sm:grid-cols-3">
        <input
          className={inputClass}
          placeholder="Display name"
          value={r.techDraft.display_name}
          onChange={(e) => r.setTechDraft((d) => ({ ...d, display_name: e.target.value }))}
        />
        <input
          className={inputClass}
          placeholder="Phone"
          value={r.techDraft.phone}
          onChange={(e) => r.setTechDraft((d) => ({ ...d, phone: e.target.value }))}
        />
        <input
          className={inputClass}
          placeholder="Skills (comma-separated)"
          value={r.techDraft.skill_tags}
          onChange={(e) => r.setTechDraft((d) => ({ ...d, skill_tags: e.target.value }))}
        />
      </div>

      <ResourceNoteFields
        noteDraft={r.noteDraft}
        setNoteDraft={r.setNoteDraft}
        onDismiss={
          activeNoteId
            ? () => void r.dismissNote(activeNoteId)
            : undefined
        }
        dismissBusy={r.actionBusy}
      />

      <button
        type="button"
        className={`${primaryBtnClass} mt-3 mb-4`}
        disabled={r.actionBusy}
        onClick={() => void r.saveTech()}
      >
        {r.actionBusy ? "Saving…" : r.techDraft.id ? "Save tech" : "Add tech"}
      </button>

      {r.scopedTechs.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">No technicians in this scope yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
          {r.pagedTechs.map((tech) => {
            const selected = r.selectedTechId === tech.id;
            const alert = r.notesForResource("tech", tech.id)[0];
            return (
              <li key={tech.id}>
                <button
                  type="button"
                  className={
                    "flex w-full flex-wrap items-center justify-between gap-2 py-2 text-left rounded-lg px-1 -mx-1 " +
                    (selected
                      ? "bg-indigo-50 ring-1 ring-indigo-200 dark:bg-indigo-950/40 dark:ring-indigo-800"
                      : "hover:bg-gray-50 dark:hover:bg-zinc-900/50")
                  }
                  onClick={() => r.selectTech(tech)}
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                      {alert ? <span className="mr-1 text-amber-600">!</span> : null}
                      {tech.display_name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-zinc-400">
                      {tech.phone || "—"}
                      {alert ? ` · ${alert.body.slice(0, 48)}${alert.body.length > 48 ? "…" : ""}` : ""}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <PaginationBar page={r.techPage} pages={r.techPages} total={r.scopedTechs.length} onPage={r.setTechPage} />
    </>
  );
}
